const PAGE_SIZE = 10;

import { useEffect, useMemo, useState } from "react";
import { apiGet, fmt } from "@/lib/api";
import { market } from "@/lib/symbols";
import { JournalPanel } from "@/components/platform/JournalPanel";
import { localDateKey, localTimeKey } from "@/lib/terminalUtils";

const chip = (ok) => ok
  ? "border-buy/40 bg-buy/10 text-buy"
  : "border-sell/40 bg-sell/10 text-sell";

const normalizeLiquidityFlow = (r) => {
  const closedAt = r.closed_at || null;
  const entryAt = r.entry_at || r.created_at || null;

  let durationMin = null;

  if (closedAt && entryAt) {
    const start = new Date(entryAt).getTime();
    const end = new Date(closedAt).getTime();

    if (Number.isFinite(start) && Number.isFinite(end)) {
      durationMin = Math.max(0, Math.floor((end - start) / 60000));
    }
  }

  const result = String(r.result || "").toUpperCase();

  return {
    id: `LF:${r.signal_id || r.fingerprint || Math.random()}`,
    source: "LIQUIDITY_FLOW",
    signal_id: r.signal_id,

    symbol: r.symbol,
    direction: r.direction,

    entry: r.entry,
    sl: r.sl,
    tp1: r.tp1,
    tp2: r.tp2,

    exit: r.exit_price ?? null,

    result:
      result === "WIN" ? "WIN" :
      result === "LOSS" ? "LOSS" :
      result || null,

    pips: Number(
      r.realized_pips ??
      r.pips ??
      0
    ),

    score: r.score ?? null,
    confidence: r.score ?? null,

    duration_min: durationMin,

    status:
      r.status === "CLOSED"
        ? "CLOSED"
        : r.status || "—",

    opened_at: entryAt,
    closed_at: closedAt,

    win: result === "WIN",
    strategy: "LIQUIDITY FLOW",
  };
};

const RunningTable = ({ items }) => (
  <div
    className="overflow-x-auto border border-white/10 bg-[#0d1219]"
    data-testid="running-signals-table"
  >
    <table className="w-full min-w-[900px] text-left font-mono text-[11px]">
      <thead>
        <tr className="border-b border-white/10 text-[9px] uppercase tracking-widest text-mute-dim">
          <th className="px-3 py-2.5">Waktu</th>
          <th className="px-3 py-2.5">Symbol</th>
          <th className="px-3 py-2.5">Arah</th>
          <th className="px-3 py-2.5">Entry</th>
          <th className="px-3 py-2.5">SL</th>
          <th className="px-3 py-2.5">TP1</th>
          <th className="px-3 py-2.5">TP2</th>
          <th className="px-3 py-2.5">Harga</th>
          <th className="px-3 py-2.5 text-right">Float Pips</th>
          <th className="px-3 py-2.5">RR</th>
          <th className="px-3 py-2.5">Conf</th>
          <th className="px-3 py-2.5">Source</th>
        </tr>
      </thead>

      <tbody>
        {items.map((r) => (
          <tr
            key={r.id}
            className="border-b border-white/5"
            data-testid={`running-${String(r.symbol).toLowerCase()}`}
          >
            <td className="px-3 py-2.5 text-mute-dim">
              {r.opened_at
                ? `${localDateKey(r.opened_at)} ${localTimeKey(r.opened_at)}`
                : "—"}
            </td>

            <td className="px-3 py-2.5 font-bold text-white">
              {r.symbol}
            </td>

            <td
              className={`px-3 py-2.5 font-bold ${
                r.direction === "BUY" ? "text-buy" : "text-sell"
              }`}
            >
              {r.direction}
            </td>

            <td className="px-3 py-2.5 text-mute">
              {r.entry}
            </td>

            <td className="px-3 py-2.5 text-sell">
              {r.sl}
            </td>

            <td className="px-3 py-2.5 text-buy">
              {r.tp1 ?? "—"}
            </td>

            <td className="px-3 py-2.5 text-buy">
              {r.tp2 ?? "—"}
            </td>

            <td className="px-3 py-2.5 text-white">
              {fmt(r.current_price, market[r.symbol]?.d ?? 2)}
            </td>

            <td
              className={`px-3 py-2.5 text-right font-bold ${
                (r.floating_pips ?? 0) >= 0
                  ? "text-buy"
                  : "text-sell"
              }`}
            >
              {r.floating_pips != null
                ? `${r.floating_pips >= 0 ? "+" : ""}${r.floating_pips}`
                : "—"}
            </td>

            <td className="px-3 py-2.5 text-mute">
              {r.rr ?? "—"}
            </td>

            <td className="px-3 py-2.5 text-white">
              {r.score ?? "—"}
            </td>

            <td className="px-3 py-2.5">
              <span className="border border-white/15 px-1.5 py-0.5 text-[8px] font-bold text-mute">
                {r.source === "LIQUIDITY_FLOW"
                  ? "LIQUIDITY FLOW"
                  : "ENGINE"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ClosedTable = ({ items }) => (
  <div
    className="overflow-x-auto border border-white/10 bg-[#0d1219]"
    data-testid="signal-history-table"
  >
    <table className="w-full min-w-[1300px] text-left font-mono text-[11px]">
      <thead>
        <tr className="border-b border-white/10 text-[9px] uppercase tracking-widest text-mute-dim">
          <th className="px-3 py-2.5">Date</th>
          <th className="px-3 py-2.5">Time</th>
          <th className="px-3 py-2.5">Symbol</th>
          <th className="px-3 py-2.5">Direction</th>
          <th className="px-3 py-2.5">Entry</th>
          <th className="px-3 py-2.5">SL</th>
          <th className="px-3 py-2.5">TP1</th>
          <th className="px-3 py-2.5">TP2</th>
          <th className="px-3 py-2.5">Exit</th>
          <th className="px-3 py-2.5">Result</th>
          <th className="px-3 py-2.5 text-right">Pips</th>
          <th className="px-3 py-2.5">Score/Confidence</th>
          <th className="px-3 py-2.5">Duration</th>
          <th className="px-3 py-2.5">Status</th>
          <th className="px-3 py-2.5">Source</th>
        </tr>
      </thead>

      <tbody>
        {items.map((r) => (
          <tr
            key={r.id}
            className="border-b border-white/5"
          >
            <td className="px-3 py-2.5 text-mute-dim">
              {r.closed_at
                ? localDateKey(r.closed_at)
                : "—"}
            </td>

            <td className="px-3 py-2.5 text-mute-dim">
              {r.closed_at
                ? localTimeKey(r.closed_at)
                : "—"}
            </td>

            <td className="px-3 py-2.5 font-bold text-white">
              {r.symbol}
            </td>

            <td
              className={`px-3 py-2.5 font-bold ${
                r.direction === "BUY"
                  ? "text-buy"
                  : "text-sell"
              }`}
            >
              {r.direction}
            </td>

            <td className="px-3 py-2.5 text-mute">
              {r.entry}
            </td>

            <td className="px-3 py-2.5 text-sell">
              {r.sl}
            </td>

            <td className="px-3 py-2.5 text-buy">
              {r.tp1 ?? "—"}
            </td>

            <td className="px-3 py-2.5 text-buy">
              {r.tp2 ?? "—"}
            </td>

            <td className="px-3 py-2.5 text-white">
              {r.exit ?? "—"}
            </td>

            <td className="px-3 py-2.5">
              <span
                className={`border px-1.5 py-0.5 text-[9px] font-bold ${
                  chip(r.win)
                }`}
              >
                {r.result || "—"}
              </span>
            </td>

            <td
              className={`px-3 py-2.5 text-right font-bold ${
                Number(r.pips ?? 0) >= 0
                  ? "text-buy"
                  : "text-sell"
              }`}
            >
              {Number(r.pips ?? 0) >= 0 ? "+" : ""}
              {Number(r.pips ?? 0).toFixed(1)}
            </td>

            <td className="px-3 py-2.5 text-white">
              {r.score ?? "—"}
              {r.source === "LIQUIDITY_FLOW" && r.score != null
                ? " / 100"
                : ""}
            </td>

            <td className="px-3 py-2.5 text-mute">
              {r.duration_min != null
                ? `${r.duration_min} mnt`
                : "—"}
            </td>

            <td className="px-3 py-2.5">
              <span className="border border-white/15 px-1.5 py-0.5 text-[9px] text-mute">
                {r.status || "CLOSED"}
              </span>
            </td>

            <td className="px-3 py-2.5">
              <span
                className={`border px-1.5 py-0.5 text-[8px] font-bold ${
                  r.source === "LIQUIDITY_FLOW"
                    ? "border-buy/40 text-buy"
                    : "border-white/15 text-mute"
                }`}
              >
                {r.source === "LIQUIDITY_FLOW"
                  ? "LIQUIDITY FLOW"
                  : "ENGINE"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const HistoryPage = () => {
  const [items, setItems] = useState(null);
  const [liquidityItems, setLiquidityItems] = useState([]);
  const [symbolFilter, setSymbolFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const liquiditySymbols = useMemo(() => {
    const base = [
      "XAUUSD",
      "BTCUSD",
      "EURUSD",
      "GBPUSD",
      "USDJPY",
    ];

    const marketSymbols = Object.keys(market || {});

    return [
      ...new Set([
        ...base,
        ...marketSymbols,
      ]),
    ];
  }, []);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const result = await apiGet("/signal-records");

        if (alive) {
          setItems(result?.data?.items || []);
        }
      } catch {
        if (alive) {
          setItems([]);
        }
      }
    };

    load();

    const t = setInterval(load, 15000);

    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadLiquidityHistory = async () => {
      try {
        const responses = await Promise.all(
          liquiditySymbols.map(async (symbol) => {
            try {
              const response = await apiGet(
                `/liquidity-flow/${symbol}/history/list`
              );

              return response?.data || [];
            } catch {
              return [];
            }
          })
        );

        if (!alive) {
          return;
        }

        const normalized = responses
          .flat()
          .map(normalizeLiquidityFlow);

        const unique = new Map();

        for (const item of normalized) {
          const key =
            item.signal_id ||
            item.id;

          if (!unique.has(key)) {
            unique.set(key, item);
          }
        }

        setLiquidityItems(
          Array.from(unique.values())
        );
      } catch {
        if (alive) {
          setLiquidityItems([]);
        }
      }
    };

    loadLiquidityHistory();

    const t = setInterval(
      loadLiquidityHistory,
      15000
    );

    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [liquiditySymbols]);

  const engineRunning = (items || []).filter(
    (r) => r.status === "RUNNING"
  );

  const liquidityRunning = liquidityItems
    .filter((r) =>
      ["PENDING_ENTRY", "OPEN", "TP1_HIT"].includes(r.status)
    )
    .map((r) => ({
      ...r,
      current_price: r.entry,
      floating_pips: null,
      rr: null,
    }));

  const running = [
    ...engineRunning.map((r) => ({
      ...r,
      source: "ENGINE",
    })),
    ...liquidityRunning,
  ];

  const engineClosed = (items || [])
    .filter((r) => r.status === "CLOSED")
    .map((r) => ({
      ...r,
      source: "ENGINE",
    }));

  const liquidityClosed = liquidityItems.filter(
    (r) => r.status === "CLOSED"
  );

  const closed = [
    ...engineClosed,
    ...liquidityClosed,
  ].sort((a, b) => {
    const ta = new Date(
      a.closed_at || a.opened_at || a.created_at || 0
    ).getTime();

    const tb = new Date(
      b.closed_at || b.opened_at || b.created_at || 0
    ).getTime();

    return tb - ta;
  });

  const symbols = useMemo(() => {
    const base = [
      "ALL",
      "XAUUSD",
      "BTCUSD",
      "EURUSD",
      "GBPUSD",
      "USDJPY",
    ];

    const extra = [
      ...new Set(
        closed
          .map((r) => r.symbol)
          .filter(Boolean)
      ),
    ];

    return [
      ...base,
      ...extra.filter(
        (s) => !base.includes(s)
      ),
    ];
  }, [closed]);

  const filteredClosed = useMemo(
    () =>
      closed.filter((r) => {
        const symbolOk =
          symbolFilter === "ALL" ||
          r.symbol === symbolFilter;

        const date = localDateKey(
          r.closed_at ||
          r.opened_at ||
          r.created_at
        );

        const fromOk =
          !fromDate ||
          date >= fromDate;

        const toOk =
          !toDate ||
          date <= toDate;

        return (
          symbolOk &&
          fromOk &&
          toOk
        );
      }),
    [
      closed,
      symbolFilter,
      fromDate,
      toDate,
    ]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredClosed.length / PAGE_SIZE
    )
  );

  const currentPage = Math.min(
    page,
    totalPages
  );

  const pagedClosed = filteredClosed.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const wins = filteredClosed.filter(
    (r) =>
      r.win === true ||
      String(r.result || "").toUpperCase() === "WIN"
  ).length;

  const losses = filteredClosed.filter(
    (r) =>
      r.win === false ||
      String(r.result || "").toUpperCase() === "LOSS"
  ).length;

  const totalPips = filteredClosed.reduce(
    (sum, r) =>
      sum + Number(r.pips || 0),
    0
  );

  const averagePips =
    filteredClosed.length
      ? totalPips / filteredClosed.length
      : 0;

  const winRate =
    filteredClosed.length
      ? (wins / filteredClosed.length) * 100
      : 0;

  useEffect(() => {
    setPage(1);
  }, [
    symbolFilter,
    fromDate,
    toDate,
  ]);

  return (
    <div
      className="space-y-5"
      data-testid="history-page"
    >
      <div>
        <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
          Running Signal{" "}
          <span className="text-buy">
            ({running.length})
          </span>
        </p>

        {running.length === 0 ? (
          <div className="border border-white/10 bg-[#0d1219] p-6 text-center font-mono text-xs text-mute-dim">
            &gt; tidak ada sinyal berjalan —
            menunggu setup VALID dari engine
          </div>
        ) : (
          <RunningTable items={running} />
        )}
      </div>

      <div>
        <div className="mb-3 flex flex-col gap-3 border border-white/10 bg-[#0d1219] p-3 sm:flex-row sm:flex-wrap sm:items-end">
          <label className="flex min-w-[150px] flex-1 flex-col gap-1 font-mono text-[9px] uppercase tracking-widest text-mute-dim">
            Symbol

            <select
              value={symbolFilter}
              onChange={(e) =>
                setSymbolFilter(e.target.value)
              }
              className="h-9 border border-white/10 bg-[#111821] px-2 text-xs text-white outline-none"
            >
              {symbols.map((symbol) => (
                <option
                  key={symbol}
                  value={symbol}
                >
                  {symbol}
                </option>
              ))}
            </select>
          </label>

          <label className="flex min-w-[140px] flex-1 flex-col gap-1 font-mono text-[9px] uppercase tracking-widest text-mute-dim">
            From

            <input
              type="date"
              value={fromDate}
              onChange={(e) =>
                setFromDate(e.target.value)
              }
              className="h-9 border border-white/10 bg-[#111821] px-2 text-xs text-white outline-none"
            />
          </label>

          <label className="flex min-w-[140px] flex-1 flex-col gap-1 font-mono text-[9px] uppercase tracking-widest text-mute-dim">
            To

            <input
              type="date"
              value={toDate}
              onChange={(e) =>
                setToDate(e.target.value)
              }
              className="h-9 border border-white/10 bg-[#111821] px-2 text-xs text-white outline-none"
            />
          </label>

          <button
            type="button"
            onClick={() => {
              setSymbolFilter("ALL");
              setFromDate("");
              setToDate("");
              setPage(1);
            }}
            className="h-9 border border-white/10 px-4 font-mono text-[9px] uppercase tracking-widest text-mute hover:bg-white/5"
          >
            Reset
          </button>
        </div>

        <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
          Signal History{" "}
          <span className="text-mute">
            ({filteredClosed.length} hasil)
          </span>
        </p>

        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            [
              "Win Rate",
              `${winRate.toFixed(1)}%`,
            ],
            [
              "Jumlah Trade",
              filteredClosed.length,
            ],
            ["WIN", wins],
            ["LOSS", losses],
            [
              "Total Pips",
              `${totalPips >= 0 ? "+" : ""}${totalPips.toFixed(1)}`,
            ],
            [
              "Average Pips",
              `${averagePips >= 0 ? "+" : ""}${averagePips.toFixed(1)}`,
            ],
          ].map(([label, value]) => (
            <div
              key={label}
              className="border border-white/10 bg-[#0d1219] p-3"
            >
              <div className="font-mono text-[8px] uppercase tracking-widest text-mute-dim">
                {label}
              </div>

              <div className="mt-1 font-mono text-sm font-bold text-white">
                {value}
              </div>
            </div>
          ))}
        </div>

        {filteredClosed.length === 0 ? (
          <div className="border border-white/10 bg-[#0d1219] p-6 text-center font-mono text-xs text-mute-dim">
            &gt; tidak ada data yang sesuai dengan filter
          </div>
        ) : (
          <ClosedTable
            items={pagedClosed}
          />
        )}

        {filteredClosed.length > 0 && (
          <div className="mt-3 flex flex-col gap-2 border border-white/10 bg-[#0d1219] p-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-mono text-[10px] text-mute-dim">
              Page {currentPage} / {totalPages}
              {" · "}
              {filteredClosed.length} hasil
            </span>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() =>
                  setPage((p) =>
                    Math.max(1, p - 1)
                  )
                }
                className="border border-white/10 px-3 py-2 font-mono text-[9px] uppercase text-mute disabled:opacity-30"
              >
                ← Prev
              </button>

              <button
                type="button"
                disabled={
                  currentPage >= totalPages
                }
                onClick={() =>
                  setPage((p) =>
                    Math.min(
                      totalPages,
                      p + 1
                    )
                  )
                }
                className="border border-white/10 px-3 py-2 font-mono text-[9px] uppercase text-mute disabled:opacity-30"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <div>
        <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
          Jurnal Paper Trade Saya
        </p>

        <JournalPanel />
      </div>
    </div>
  );
};
